import * as React from 'react';

import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';

import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { PointConfigurationData } from '@/services/pointConfiguration/pointConfiguration.types';
import { useSearch } from '@tanstack/react-router';
import { useDeletePointCategory, useUpdatePointCategoryStatus } from '../hooks/usePointCategoryMutations';
import { getColumns } from './Column';
import { getModalConfig, getTitleByActiveTab, TabType } from './helper';

export function PointsConfigurationTable({
  data,
  isLoading,
  pagination,
}: {
  data: PointConfigurationData[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { activeTab } = useSearch({ from: '/(app)/points-management/points-configuration/' });
  const currentTab = activeTab ?? 'affiliate';
  const { mutateAsync: updateStatus } = useUpdatePointCategoryStatus();
  const { mutateAsync: deleteCategory } = useDeletePointCategory();
  const canCreate = useCan({ allOf: [PERMS.point.management.create] });
  const { t } = useDynamicLocale();

  const canViewSlabList = useCan({ allOf: [PERMS.point.management.slab.list] });

  const columns = React.useMemo(
    () => getColumns(onOpen, currentTab, updateStatus, deleteCategory, openConfirmation, t, canViewSlabList),
    [onOpen, currentTab, updateStatus, deleteCategory, openConfirmation, t, canViewSlabList],
  );
  const title = getTitleByActiveTab(currentTab, t);

  const onAddnew = (activeTab: TabType) => {
    onOpen(getModalConfig(activeTab, t));
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pagination.totalPages,
    initialState: {
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      title,
      action: canCreate ? () => onAddnew(currentTab) : null,
      actionKey: canCreate ? 'onAddNew' : '',
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => originalRow.pointCategoryId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable className="rounded-t-none" table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
